import dynamic from 'next/dynamic'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useMediaQuery } from 'react-responsive'
import { BottomSheet } from 'react-spring-bottom-sheet'

import { SupportTab } from '@/components/atomic/SupportTab'
import { TextSearch } from '@/components/search/inputs/TextSearch'
import { Button, ButtonHierarchy } from '@/components/styleguide/Button'
import SearchResults from '@/components/views/SearchResults'
import { SearchResultsToolbar } from '@/components/views/SearchResultsToolbar'
import {
  constants,
  FILTER_DELIVERY,
  FILTER_INCLUSIVE_BENEFITS,
  FILTER_INITIAL_PAYMENT,
  FILTER_MILEAGES,
  FILTER_MONTHLY_PAYMENT,
  FILTER_SORT_BY,
  FILTER_TERMS,
} from '@/lib/constants'
import { LOADERS } from '@/lib/loaders'
import { getWithExpiry, storageAvailable } from '@/lib/utilities/system'
import { addToDataLayer } from '@/lib/utilities/vehicle'
import { clearAllSearchState, openSearchFilter } from '@/store/reducers/search'
import { storeShowBenefitsModal } from '@/store/reducers/system'
import { selectLoaderExists } from '@/store/selectors/loader'
import { selectCountOfSearchParameters, selectVehicleTotal } from '@/store/selectors/search'
import { Size } from '@/types/system'

const SearchFilters = dynamic(() => import('@/components/search/SearchFilters'), {
  ssr: false,
})
const SearchPills = dynamic(() => import('@/components/views/SearchPills'), {
  ssr: false,
})

const PADDING_BOTTOM = 40

export const SearchResultsAndFilters = ({ isVans, status, setStatus }) => {
  const dispatch = useDispatch()
  const isMobile = useMediaQuery({ maxWidth: 991 })
  const fetchingSearchResultsLoader = useSelector(state =>
    selectLoaderExists(state, LOADERS.fetchingSearchResults),
  )
  const filterCount = useSelector(selectCountOfSearchParameters)
  const vehicleTotal = useSelector(selectVehicleTotal)
  const [areFiltersVisible, setAreFiltersVisible] = useState(true)
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false)
  const [layoutType, setLayoutType] = useState('')
  const [visibleFilters, setVisibleFilters] = useState([])
  const [toggleId, setToggleId] = useState('')

  useEffect(() => {
    if (storageAvailable('localStorage')) {
      const storageKey = getWithExpiry(constants.localStorageKey.browseLayoutType)
      if (storageKey) {
        addToDataLayer({
          browseLayout: storageKey,
        })
        setLayoutType(storageKey)
      } else {
        let defaultLayoutType = 'grid'
        setLayoutType(defaultLayoutType)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const handleScroll = () => {
      const filterControls = document.querySelector('.search__filter-controls')
      const filterControlsYCoord = filterControls.getBoundingClientRect().y

      const calculatedPadding = filterControlsYCoord > 0 ? filterControlsYCoord : 0

      filterControls.style.paddingBottom = `${calculatedPadding + PADDING_BOTTOM}px`
    }

    window.addEventListener('scroll', handleScroll)

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  useEffect(() => {
    if (typeof window.fbq !== 'undefined') {
      window.fbq('track', 'Search', {
        // eslint-disable-next-line camelcase
        content_type: 'vehicle_offer',
      })
    }
  }, [])

  useEffect(() => {
    dispatch(storeShowBenefitsModal(true))
  }, [dispatch])

  useEffect(() => {
    if (fetchingSearchResultsLoader) {
      setStatus(constants.componentStatus.LOADING)
    } else {
      setStatus(constants.componentStatus.COMPLETE)
    }
  }, [fetchingSearchResultsLoader, setStatus, status])

  const toggleMobileFilters = () => {
    setIsMobileFiltersOpen(prev => !prev)
  }

  const toggleFilter = id => {
    const isFilterVisible = visibleFilters.includes(id)
    if (!isFilterVisible) {
      dispatch(openSearchFilter(id))
      const leaseDetails = [
        FILTER_MONTHLY_PAYMENT,
        FILTER_INITIAL_PAYMENT,
        FILTER_MILEAGES,
        FILTER_TERMS,
        FILTER_DELIVERY,
        FILTER_INCLUSIVE_BENEFITS,
        FILTER_SORT_BY,
      ]
      if (leaseDetails.includes(id)) {
        setVisibleFilters(prev => [...prev, id])
      } else {
        setToggleId(id)
      }
    } else {
      setVisibleFilters(prev => prev.filter(item => item !== id))
    }
  }

  return (
    <div className="container--search-page">
      <div className="mobile-filters">
        {isMobile && (
          <TextSearch
            visibleFilters={visibleFilters}
            isMobile={true}
          />
        )}
        <Button
          size={Size.Medium}
          hierarchy={ButtonHierarchy.Black}
          className="search__filter-button"
          onClick={toggleMobileFilters}
          icon="SlidersHorizontal"
        />
      </div>
      <div className="search-pills">
        <SearchPills hasPadding={false} />
      </div>
      {isMobile && (
        <BottomSheet
          expandOnContentDrag
          scrollLocking={false}
          open={isMobileFiltersOpen}
          onDismiss={toggleMobileFilters}
          snapPoints={({ maxHeight }) => [maxHeight - 8]}
          header={
            <div className={'mobile-filters-sheet-header'}>
              <Button
                label="Cancel"
                hierarchy={ButtonHierarchy.Text}
                size={Size.Large}
                className="header-button"
                onClick={toggleMobileFilters}
              />
              <h2>Filters</h2>
            </div>
          }
          footer={
            <div className={'mobile-filters-sheet-footer'}>
              <Button
                hasUnderline
                size={Size.Large}
                className="clear-button"
                hierarchy={ButtonHierarchy.Text}
                label={`Clear all (${filterCount})`}
                disabled={filterCount === 0}
                onClick={() => {
                  setVisibleFilters([])
                  dispatch(clearAllSearchState())
                }}
              />
              <Button
                size={Size.Large}
                hierarchy={ButtonHierarchy.Filled}
                retainWidthOnLoadingState
                isLoading={fetchingSearchResultsLoader}
                label={`Search ${isVans ? 'vans' : 'cars'}`}
                disabled={vehicleTotal === 0}
                onClick={toggleMobileFilters}
              />
            </div>
          }>
          <div className="mobile-filters-sheet">
            <SearchFilters
              isMobile
              toggleFilter={toggleFilter}
              isVans={isVans}
              setIsMobileFiltersOpen={setIsMobileFiltersOpen}
              visibleFilters={visibleFilters}
              setVisibleFilters={setVisibleFilters}
              toggleId={toggleId}
              setToggleId={setToggleId}
            />
          </div>
        </BottomSheet>
      )}
      <div className="search-page-wrapper">
        <div className={`container--search ${!areFiltersVisible ? 'hidden-filters' : ''}`}>
          <div className="search__filter-controls">
            <SearchFilters
              visibleFilters={visibleFilters}
              setVisibleFilters={setVisibleFilters}
              toggleFilter={toggleFilter}
              isMobile={false}
              isVans={isVans}
              toggleId={toggleId}
              setToggleId={setToggleId}
            />
          </div>
          <div className="search__container">
            <SearchResultsToolbar
              setAreFiltersVisible={setAreFiltersVisible}
              areFiltersVisible={areFiltersVisible}
              isVans={isVans}
              layoutType={layoutType}
              setLayoutType={setLayoutType}
            />
            <SearchResults
              layoutType={layoutType}
              isVans={isVans}
              status={status}
            />
          </div>
          <SupportTab />
        </div>
      </div>
    </div>
  )
}
